import { z } from 'zod';
import type { ProductionPlaceCoordinates } from '@/types/core/production-declaration/production-place';

export const GEOJSON_MAX_FILE_BYTES = 5 * 1024 * 1024;
export const GEOJSON_MAX_VERTICES = 10_000;

const longitude = z.number().min(-180).max(180);
const latitude = z.number().min(-90).max(90);
const positionSchema = z
  .union([
    z.tuple([longitude, latitude]),
    z.tuple([longitude, latitude, z.number()]),
  ])
  .transform(([lng, lat]): [number, number] => [lng, lat]);
const ringSchema = z
  .array(positionSchema)
  .min(4)
  .refine((ring) => {
    const first = ring[0];
    const last = ring.at(-1);

    return (
      first &&
      last &&
      first[0] === last[0] &&
      first[1] === last[1] &&
      new Set(ring.map(([lng, lat]) => `${lng},${lat}`)).size >= 3
    );
  });
const polygonSchema = z.array(ringSchema).min(1);
const geometryEnvelopeSchema = z.object({
  type: z.enum(['Polygon', 'MultiPolygon']),
  coordinates: z.unknown(),
  crs: z.never().optional(),
});
const featureSchema = z.object({
  type: z.literal('Feature'),
  geometry: geometryEnvelopeSchema,
  crs: z.never().optional(),
});
const documentSchema = z.union([
  geometryEnvelopeSchema,
  featureSchema,
  z.object({
    type: z.literal('FeatureCollection'),
    features: z.array(featureSchema).min(1).max(GEOJSON_MAX_VERTICES),
    crs: z.never().optional(),
  }),
]);
const geometrySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('Polygon'), coordinates: polygonSchema }),
  z.object({
    type: z.literal('MultiPolygon'),
    coordinates: z.array(polygonSchema).min(1),
  }),
]);

export type GeoJsonImportError =
  | 'fileTooLarge'
  | 'invalidGeoJson'
  | 'tooManyVertices';
type GeoJsonImportResult =
  | { status: 'success'; geometry: ProductionPlaceCoordinates }
  | { status: 'error'; error: GeoJsonImportError };

function hasTooManyVertices(
  geometries: z.infer<typeof geometryEnvelopeSchema>[],
): boolean {
  let vertices = 0;

  for (const geometry of geometries) {
    const arrays: unknown[] = [geometry.coordinates];

    while (arrays.length > 0) {
      const item = arrays.pop();

      if (!Array.isArray(item)) {
        continue;
      }

      if (typeof item[0] === 'number') {
        vertices++;
      } else {
        for (const child of item) {
          arrays.push(child);
        }
      }

      if (vertices > GEOJSON_MAX_VERTICES) {
        return true;
      }
    }
  }

  return false;
}

export function parseProductionPlaceGeoJson(text: string): GeoJsonImportResult {
  if (new TextEncoder().encode(text).length > GEOJSON_MAX_FILE_BYTES) {
    return { status: 'error', error: 'fileTooLarge' };
  }

  let input: unknown;

  try {
    input = JSON.parse(text);
  } catch {
    return { status: 'error', error: 'invalidGeoJson' };
  }

  const document = documentSchema.safeParse(input);

  if (!document.success) {
    return { status: 'error', error: 'invalidGeoJson' };
  }

  const geometries =
    document.data.type === 'FeatureCollection'
      ? document.data.features.map((feature) => feature.geometry)
      : [
          document.data.type === 'Feature'
            ? document.data.geometry
            : document.data,
        ];

  if (hasTooManyVertices(geometries)) {
    return { status: 'error', error: 'tooManyVertices' };
  }

  const parsed = z.array(geometrySchema).safeParse(geometries);

  if (!parsed.success) {
    return { status: 'error', error: 'invalidGeoJson' };
  }

  const polygons = parsed.data.flatMap((geometry) =>
    geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates,
  );

  return {
    status: 'success',
    geometry:
      polygons.length === 1
        ? { type: 'Polygon', coordinates: polygons[0] }
        : { type: 'MultiPolygon', coordinates: polygons },
  };
}
